import MsgReader, { FieldsData } from '@kenjiuno/msgreader';
import { convertToBase64 } from 'lib/PublicFunctions';
import { FileUpload } from './UploadCenter/UploadContin/types';
import { parseEml, ParsedEmlJson } from 'eml-parse-js';

export interface EmailFields {
  subject: string;
  senderEmail: string;
  ccEmails: string[];
}


export const getMsgFileMetaData: (file: File) => Promise<EmailFields> = async (file: File) => {
  const base64 = (await convertToBase64(file)) as string;
  const msgDataHandler = new MsgReader(Buffer.from(base64, 'base64'));
  const messageData = msgDataHandler.getFileData();
  const ccEmails = excludeCcEmails(
    messageData.recipients.filter(r => r.recipType === 'cc').map(r => r.smtpAddress)
  );
  return {
    subject: messageData.subject || '',
    senderEmail: messageData.senderEmail?.includes('@')
      ? messageData.senderEmail
      : messageData.senderSmtpAddress,
    ccEmails,
  };
};

export const getEmlFileMetaData: (file: File) => Promise<EmailFields> = async (file: File) => {
  const fileBase64 = (await convertToBase64(file)) as string;
  const buffer = Buffer.from(fileBase64, 'base64');
  const emlText = buffer.toString('utf-8');
  const parsed = parseEml(emlText) as ParsedEmlJson;
  const ccEmails = excludeCcEmails(parsed.headers.CC?.split(',') || []);

  return {
    subject: parsed.headers.Subject || '',
    senderEmail: parsed.headers.From || '',
    ccEmails,
  };
};

const excludeCcEmails = (ccEmails: string[]) => {
  return ccEmails.filter(
    e => e.toLowerCase().includes('@') && !e.toLowerCase().includes('@hudsonsearch.com')
  );
};

export const getMsgFileMetaDataFromBase64: (fileBase64: string) => Promise<EmailFields> = async (
  fileBase64: string
) => {
  const msgDataHandler = new MsgReader(Buffer.from(fileBase64, 'base64'));
  const messageData = msgDataHandler.getFileData();
  const ccEmails = excludeCcEmails(
    messageData.recipients.filter(r => r.recipType === 'cc').map(r => r.smtpAddress)
  );
  return {
    subject: messageData.subject || '',
    senderEmail: messageData.senderEmail?.includes('@')
      ? messageData.senderEmail
      : messageData.senderSmtpAddress,
    ccEmails,
  };
};

export const getMsgFileMetaDataWithAttachments: (
  file: File,
  extensionOfInterest: string
) => Promise<{
  messageData: EmailFields;
  attachmentData?: FileUpload[];
}> = async (file, extensionOfInterest = '.pdf') => {
  const base64 = (await convertToBase64(file)) as string;
  const msgDataHandler = new MsgReader(Buffer.from(base64, 'base64'));
  const messageData = msgDataHandler.getFileData();

  const pdfAttachments = messageData.attachments
    .map((_, index) => msgDataHandler.getAttachment(index))
    .filter(attachment => attachment.fileName.toLowerCase().endsWith(extensionOfInterest));

  const ccEmails = excludeCcEmails(
    messageData.recipients.filter(r => r.recipType === 'cc').map(r => r.smtpAddress)
  );
  const emailFields: EmailFields = {
    subject: messageData.subject || '',
    senderEmail: messageData.senderEmail?.includes('@')
      ? messageData.senderEmail
      : messageData.senderSmtpAddress,
    ccEmails,
  };

  return {
    messageData: emailFields,
    attachmentData: pdfAttachments.map(pdfAttachment =>
      createBase64File(pdfAttachment.content, pdfAttachment.fileName)
    ),
  };
};

function createBase64File(content: Uint8Array, fileName: string): FileUpload {
  const base64Content = Buffer.from(content).toString('base64');
  return {
    actualFileName: fileName,
    file: base64Content,
  };
}
